import React from 'react';
import styled, { css } from 'styled-components';

// molekule Icon renders material design icons via className "mdi mdi-<name>"
// The MDI font CSS is loaded separately by the app.
// Props: name (required), size (number → px), color (string, resolved from theme.colors),
//        disabled (bool), onClick (func)

export interface IconProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'color'
> {
  name: string;
  size?: number;
  color?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

const Icon = React.forwardRef<HTMLElement, IconProps>(
  function Icon(props, ref) {
    const { name, size, color, disabled, onClick, className, ...rest } = props;

    // Resolve color from theme — styled-components provides theme via context
    // We do the resolution inline in the styled component using $color
    // But we don't have direct theme access in the wrapper function.
    // Use a theme-aware styled helper to resolve color.

    const mdiClass = `mdi mdi-${name}`;
    const combinedClass = className ? `${mdiClass} ${className}` : mdiClass;

    return (
      <StyledIconWithTheme
        ref={ref as any}
        $name={name}
        $size={size}
        $rawColor={color}
        $disabled={!!disabled}
        $clickable={!!onClick}
        className={combinedClass}
        onClick={onClick}
        {...rest}
      />
    );
  },
);

// Separate styled component that can access theme for color resolution
interface StyledIconWithThemeProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'color'
> {
  $name: string;
  $size?: number;
  $rawColor?: string;
  $disabled?: boolean;
  $clickable?: boolean;
}

const StyledIconWithTheme = styled.i<StyledIconWithThemeProps>`
  color: ${(p) => {
    if (!p.$rawColor) return 'inherit';
    const fromTheme = p.theme && p.theme.colors && p.theme.colors[p.$rawColor];
    return fromTheme || p.$rawColor;
  }};
  font-size: ${(p) => (p.$size != null ? `${p.$size}px` : 'inherit')};

  ${(p) =>
    p.$disabled &&
    css`
      pointer-events: none;
      opacity: 0.65;
    `}

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
    `}
`;

export default Icon;
